import React from 'react';
import { motion } from 'motion/react';
import { Job, JobStatus, DroneMission, User } from '../types';
import { Icons } from '../constants';
import { Logo, BrandName } from './Logo';

interface Props {
  jobs: Job[];
  missions: DroneMission[];
  onNavigate: (tab: string) => void;
  user?: User;
}

const ClientDashboardView: React.FC<Props> = ({ jobs, missions, onNavigate, user }) => {
  const activeJobs = jobs.filter(j => j.status !== JobStatus.PAID && j.status !== JobStatus.COMPLETED);
  const completedJobs = jobs.filter(j => j.status === JobStatus.PAID || j.status === JobStatus.COMPLETED);
  
  const clientJobIds = jobs.map(j => j.id);
  const clientMissions = missions.filter(m => clientJobIds.includes(m.jobId) || m.jobId.startsWith('MANUAL-'));
  const activeMissions = clientMissions.filter(m => m.status !== 'Completed');

  const upcomingJobs = jobs.filter(j => j.scheduledDate && j.status === JobStatus.SCHEDULED).sort((a, b) => new Date(a.scheduledDate!).getTime() - new Date(b.scheduledDate!).getTime());
  const pendingInvoices = jobs.filter(j => j.status === JobStatus.INVOICED);

  const containerVariants: any = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants: any = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 24 } }
  };

  return (
    <motion.div 
      variants={containerVariants}
      initial="hidden"
      animate="show"
      className="max-w-6xl mx-auto space-y-8"
    >
      {/* Welcome Header */}
      <motion.div variants={itemVariants} className="bg-bark p-10 rounded-[40px] text-white flex flex-col md:flex-row justify-between items-center relative overflow-hidden shadow-xl bg-cover bg-center" style={{ backgroundImage: "linear-gradient(to right, rgba(77, 40, 9, 0.95), rgba(77, 40, 9, 0.4)), url('/greentree-hero.jpg')" }}>
        <div className="relative z-10 text-center md:text-left">
          <div className="flex items-center space-x-3 mb-2">
            <Logo className="w-10 h-10" />
            <h2 className="text-3xl font-black">Welcome back to <span className="text-gold">GreenTree</span></h2>
          </div>
          <p className="text-stone-400 font-medium text-lg">Here is an overview of your property care.</p>
        </div>
        <div className="relative z-10 mt-6 md:mt-0 flex flex-wrap gap-4 justify-center md:justify-start">
          <button 
            onClick={() => {
              if (user?.role === 'guest') {
                onNavigate('guest-login-prompt');
              } else {
                onNavigate('client-catalog');
              }
            }}
            className="px-6 py-3 bg-gold text-bark font-black rounded-2xl hover:bg-white transition shadow-lg shadow-gold/10 uppercase tracking-widest text-xs"
          >
            Request Service
          </button>
          <button 
            onClick={() => {
              if (user?.role === 'guest') {
                onNavigate('guest-login-prompt');
              } else {
                onNavigate('client-drones');
              }
            }}
            className="px-6 py-3 bg-white/10 text-white font-black rounded-2xl hover:bg-white/20 transition uppercase tracking-widest text-xs backdrop-blur-sm"
          >
            Book Drone
          </button>
          <button 
            onClick={() => onNavigate('client-education')}
            className="px-6 py-3 bg-palm text-white font-black rounded-2xl hover:bg-gecko transition shadow-lg shadow-palm/20 uppercase tracking-widest text-xs"
          >
            Education Center
          </button>
          <button
            onClick={() => onNavigate('client-vault')}
            className="px-6 py-3 bg-white/10 text-white font-black rounded-2xl hover:bg-white/20 transition uppercase tracking-widest text-xs backdrop-blur-sm"
          >
            Property Vault
          </button>
        </div>
        <div className="absolute right-0 top-0 opacity-5 transform translate-x-10 -translate-y-10 scale-150 pointer-events-none text-gold">
           <Icons.Tree />
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Quick Stats & Upcoming */}
        <div className="lg:col-span-2 space-y-8">
          
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <motion.div variants={itemVariants} className="bg-white p-6 rounded-3xl border border-stone-200 shadow-sm flex flex-col items-center justify-center text-center hover:shadow-md transition-shadow">
              <span className="text-3xl font-black text-bark">{activeJobs.length}</span>
              <span className="text-[10px] font-black text-stone-400 uppercase tracking-widest mt-1">Active Projects</span>
            </motion.div>
            <motion.div variants={itemVariants} className="bg-white p-6 rounded-3xl border border-stone-200 shadow-sm flex flex-col items-center justify-center text-center hover:shadow-md transition-shadow">
              <span className="text-3xl font-black text-gecko">{completedJobs.length}</span>
              <span className="text-[10px] font-black text-stone-400 uppercase tracking-widest mt-1">Completed</span>
            </motion.div>
            <motion.div variants={itemVariants} className="bg-white p-6 rounded-3xl border border-stone-200 shadow-sm flex flex-col items-center justify-center text-center hover:shadow-md transition-shadow">
              <span className="text-3xl font-black text-palm">{activeMissions.length}</span>
              <span className="text-[10px] font-black text-stone-400 uppercase tracking-widest mt-1">Active Flights</span>
            </motion.div>
            <motion.div variants={itemVariants} className="bg-white p-6 rounded-3xl border border-stone-200 shadow-sm flex flex-col items-center justify-center text-center hover:shadow-md transition-shadow">
              <span className="text-3xl font-black text-orange-500">{pendingInvoices.length}</span>
              <span className="text-[10px] font-black text-stone-400 uppercase tracking-widest mt-1">Pending Invoices</span>
            </motion.div>
          </div>

          {/* Active Projects */}
          <motion.div variants={itemVariants} className="bg-white rounded-[40px] border border-stone-200 p-8 shadow-sm">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-black text-bark">Active Projects</h3>
              <button onClick={() => onNavigate('client')} className="text-xs font-bold text-palm hover:text-gecko uppercase tracking-widest">View All</button>
            </div>
            
            {activeJobs.length === 0 ? (
              <div className="text-center py-12 bg-stone-50 rounded-3xl border-2 border-dashed border-stone-200">
                <p className="text-stone-400 font-bold text-sm">No active projects.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {activeJobs.slice(0, 3).map(job => (
                  <motion.div 
                    whileHover={{ scale: 1.01 }}
                    key={job.id} 
                    className="flex items-center justify-between p-4 bg-stone-50 rounded-2xl border border-stone-100 hover:border-stone-300 transition-colors cursor-pointer" 
                    onClick={() => onNavigate('client')}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-stone-400 shadow-sm">
                        <Icons.Tree />
                      </div>
                      <div>
                        <h4 className="font-bold text-bark text-sm">{job.serviceType}</h4>
                        <p className="text-xs text-stone-500">{job.address}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`text-[10px] font-black px-2.5 py-1 rounded-full uppercase tracking-wider ${
                        job.status === JobStatus.ESTIMATING ? 'bg-blue-100 text-blue-700' :
                        job.status === JobStatus.PENDING ? 'bg-amber-100 text-amber-700' :
                        job.status === JobStatus.SCHEDULED ? 'bg-purple-100 text-purple-700' :
                        job.status === JobStatus.INVOICED ? 'bg-orange-100 text-orange-700' :
                        'bg-stone-200 text-stone-700'
                      }`}>
                        {job.status}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>

        </div>

        {/* Right Column: Invoices & Flights */}
        <div className="space-y-8">
          
          {/* Action Needed (Invoices) */}
          {pendingInvoices.length > 0 && (
            <div className="bg-orange-50 rounded-[32px] border border-orange-100 p-8 shadow-sm">
              <h3 className="text-lg font-black text-orange-800 mb-4 flex items-center space-x-2">
                <Icons.Finance />
                <span>Action Required</span>
              </h3>
              <div className="space-y-3">
                {pendingInvoices.map(job => (
                  <div key={job.id} className="bg-white p-4 rounded-2xl shadow-sm border border-orange-100">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-bold text-bark text-sm">{job.serviceType}</h4>
                      <span className="font-black text-orange-600">${(Number(job.totalPrice) || 0).toLocaleString()}</span>
                    </div>
                    <button onClick={() => onNavigate('client')} className="w-full py-2 mt-2 bg-orange-500 text-white text-xs font-bold rounded-xl hover:bg-orange-600 transition">
                      Pay Invoice
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upcoming Appointments */}
          <div className="bg-white rounded-[32px] border border-stone-200 p-8 shadow-sm">
            <h3 className="text-lg font-black text-bark mb-6">Upcoming Schedule</h3>
            {upcomingJobs.length === 0 ? (
              <p className="text-stone-400 text-sm italic text-center py-4">No upcoming appointments.</p>
            ) : (
              <div className="space-y-4">
                {upcomingJobs.map(job => (
                  <div key={job.id} className="flex space-x-4 items-start border-b border-stone-100 pb-4 last:border-0 last:pb-0">
                    <div className="w-12 h-12 bg-gecko/10 rounded-xl flex flex-col items-center justify-center text-gecko flex-shrink-0">
                      <span className="text-[10px] font-black uppercase">{new Date(job.scheduledDate!).toLocaleString('default', { month: 'short' })}</span>
                      <span className="text-lg font-black leading-none">{new Date(job.scheduledDate!).getDate()}</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-bark text-sm">{job.serviceType}</h4>
                      <p className="text-xs text-stone-500 mb-2">{job.address}</p>
                      
                      {job.commitments && job.commitments.length > 0 && (
                        <div className="mt-2 bg-stone-50 p-2 rounded-lg border border-stone-100">
                          <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest mb-1">Assigned Crew</p>
                          <div className="flex flex-wrap gap-1">
                            {job.commitments.map((c, idx) => (
                              <div key={idx} className="flex items-center space-x-1 bg-white px-2 py-1 rounded-md border border-stone-200">
                                <div className="w-4 h-4 rounded-full bg-palm text-white flex items-center justify-center font-bold text-[8px]">
                                  {(c.workerName?.charAt?.(0) ?? '?')}
                                </div>
                                <span className="text-[10px] font-bold text-stone-600">{c.workerName}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Drone Flights */}
          <div className="bg-white rounded-[32px] border border-stone-200 p-8 shadow-sm">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-black text-bark">Recent Flights</h3>
              <button onClick={() => onNavigate('client-drones')} className="text-xs font-bold text-palm hover:text-gecko uppercase tracking-widest">All</button>
            </div>
            {clientMissions.length === 0 ? (
              <p className="text-stone-400 text-sm italic text-center py-4">No drone flights requested.</p>
            ) : (
              <div className="space-y-4">
                {clientMissions.slice(0, 3).map(mission => (
                  <div key={mission.id} className="flex items-center justify-between p-3 bg-stone-50 rounded-xl border border-stone-100">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-stone-400 shadow-sm">
                        <Icons.Drone />
                      </div>
                      <div>
                        <h4 className="font-bold text-bark text-xs">{mission.missionType}</h4>
                        <span className={`text-[8px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider ${
                          mission.status === 'Completed' ? 'bg-green-100 text-green-700' : 
                          mission.status === 'Denied' ? 'bg-red-100 text-red-700' :
                          mission.status === 'Pending Approval' ? 'bg-orange-100 text-orange-700' : 
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {mission.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </motion.div>
  );
};

export default ClientDashboardView;
